/**
 * Conversations endpoints: the drill-down list, a post's breakdown, and the
 * Tune sentiment save — which invalidates every widget and every list of this
 * source, so the whole dashboard redraws with the corrected sentiment.
 */
import type {
  ConversationsListRequest,
  ConversationsPage,
  PostBreakdown,
  PostBreakdownRequest,
  SaveSentimentOverridesRequest,
} from '@/data-sources/contract';
import { api, overrideExisting } from '@/store/api/api';
import { fromSource, type Sourced } from '@/store/api/fromSource';
import { tag } from '@/store/api/tags';

export const conversationsApi = api.injectEndpoints({
  overrideExisting,
  endpoints: (build) => ({
    listConversations: build.query<ConversationsPage, Sourced<ConversationsListRequest>>({
      queryFn: fromSource((source, params, ctx) => source.listConversations(params, ctx)),
      providesTags: (_r, _e, arg) => [tag('Conversation', arg.source)],
    }),
    getPostBreakdown: build.query<PostBreakdown, Sourced<PostBreakdownRequest>>({
      queryFn: fromSource((source, params, ctx) => source.getPostBreakdown(params, ctx)),
      providesTags: (_r, _e, arg) => [
        tag('Conversation', arg.source),
        tag('Conversation', arg.source, `post:${arg.params.postId}`),
      ],
    }),
    saveSentimentOverrides: build.mutation<null, Sourced<SaveSentimentOverridesRequest>>({
      queryFn: fromSource(async (source, params, ctx) => {
        await source.saveSentimentOverrides(params, ctx);
        return null;
      }),
      /* A correction moves every sentiment figure, list and breakdown of this source. */
      invalidatesTags: (_r, error, arg) =>
        error ? [] : [tag('Widget', arg.source), tag('Conversation', arg.source)],
    }),
  }),
});

export const {
  useListConversationsQuery,
  useGetPostBreakdownQuery,
  useSaveSentimentOverridesMutation,
} = conversationsApi;
